import { Alert, Button, Group, Paper, PasswordInput, Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { forwardRef, useImperativeHandle } from "react"
import { Trans, useTranslation } from "react-i18next"
import { getErrorMessage } from "../utils"
import { useResetPassword } from "../hooks/useAdminUsers"
import type { ResetPasswordFormValues } from "../models/adminUser"

interface Props {
  userId: number
  username: string
  onSuccess: () => void
}

const ResetPasswordFormContent = forwardRef<{ submit: () => void }, Props>(
  ({ userId, username, onSuccess }, ref) => {
    const { t } = useTranslation()
    const resetMutation = useResetPassword()

    const form = useForm<ResetPasswordFormValues>({
      initialValues: { password: "" },
      validate: {
        password: (v) => (v.length >= 6 ? null : t("adminUsers.validation.passwordMin")),
      },
    })

    const handleSubmit = (values: ResetPasswordFormValues) => {
      resetMutation.mutate(
        { id: userId, password: values.password },
        {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("adminUsers.resetPasswordSuccess"),
            })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        },
      )
    }

    const handleFormSubmit = form.onSubmit(handleSubmit)
    useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }))

    return (
      <form onSubmit={handleFormSubmit}>
        {resetMutation.isError && (
          <Alert color="red" mb="md" withCloseButton onClose={() => resetMutation.reset()}>
            {resetMutation.error?.message}
          </Alert>
        )}
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <PasswordInput
              label={t("adminUsers.newPassword")}
              placeholder={t("adminUsers.newPasswordPlaceholder")}
              autoComplete="new-password"
              autoFocus
              {...form.getInputProps("password")}
            />
          </Stack>
        </Paper>
        <Group justify="flex-end" mt="md">
          <Button type="submit" loading={resetMutation.isPending}>
            <Trans i18nKey="adminUsers.resetPassword" />
          </Button>
        </Group>
      </form>
    )
  },
)

export default ResetPasswordFormContent
